/**
 * Hidden iframe bridge that syncs auth + branding to the Canvas 2.0 domain
 * (Template Management app at VITE_CANVAS_URL).
 *
 * Mirrors scio1's canvas-configuration.js:
 *   1. Loads {canvasUrl}/frame.html in a hidden iframe
 *   2. Posts {action:'set', data:{token,userId,expires,teamId}} → frame sets localStorage._auth
 *   3. Posts {action:'setBranding', data:branding} → frame sets localStorage._setBranding
 *   4. On logout posts {action:'remove'} → frame removes localStorage._auth
 *
 * Production _auth format:
 *   {"token":"...","userId":"...","expires":"Sun May 24 2026 ...","teamId":"..."}
 *
 * Production _setBranding format:
 *   {"baseUrl":"https://app.optisigns.com","branding":true,"colorTheme":"#1ab394",
 *    "favicon":"/images/shortcut-icon.png","isWhiteLabel":false,"logo":"","logoCompany":"OS",
 *    "removeOptiSignsBranding":false,"hideOptiSignsBrandedApps":false,"siteTitle":"OptiSigns",
 *    "templateCategories":[],"language":"en"}
 *
 * IMPORTANT: Canvas app (token-auth.service.ts:75-80) removes _setBranding when
 * _auth is removed via storage event. So we must NOT send {action:'remove'} during
 * initial page load when auth hasn't loaded yet — only on actual logout.
 */
import { useRef, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/stores/auth-store';
import { useTeamStore } from '@/stores/team-store';
import { resolveBranding, BRANDING_DEFAULTS } from '@scio3/shared-types';

const CANVAS_URL = import.meta.env.VITE_CANVAS_URL || 'http://localhost:4202';
const CANVAS_ORIGIN = (() => {
  try {
    return new URL(CANVAS_URL).origin;
  } catch {
    return CANVAS_URL;
  }
})();

/**
 * Resolves when CanvasAuthBridge has synced auth + branding to the Canvas domain.
 * PersistentCanvasFrame waits for this before loading the Canvas iframe.
 */
let _authSyncedResolve: (() => void) | null = null;
export const canvasAuthSynced = new Promise<void>((resolve) => {
  _authSyncedResolve = resolve;
});

export function CanvasAuthBridge() {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [isReady, setIsReady] = useState(false);
  // Track if we've ever sent auth — only send 'remove' after a successful auth (actual logout)
  const hasSentAuthRef = useRef(false);

  const token = useAuthStore((s) => s.token);
  const firebaseUser = useAuthStore((s) => s.firebaseUser);
  const me = useAuthStore((s) => s.me);
  const activeTeamId = useTeamStore((s) => s.activeTeamId);
  const { i18n } = useTranslation();

  const postToCanvas = useCallback((data: Record<string, unknown>) => {
    iframeRef.current?.contentWindow?.postMessage(JSON.stringify(data), CANVAS_ORIGIN);
  }, []);

  // Sync auth — matches production _auth format:
  // {token, userId, expires, teamId}
  // userId must be the internal Meteor user ID (me._id), NOT the Firebase UID.
  // Canvas REST API (e.g. /recent) queries designs by this userId, and designs
  // are stored with the Meteor user ID in the database.
  const syncAuth = useCallback(() => {
    if (!token || !firebaseUser || !me?._id) {
      // Only send 'remove' on actual logout (auth was previously set, now gone).
      // Skip during initial load when auth hasn't loaded yet — sending 'remove'
      // would cause canvas to also delete _setBranding via storage event.
      if (hasSentAuthRef.current) {
        postToCanvas({ action: 'remove' });
        hasSentAuthRef.current = false;
      }
      return;
    }
    hasSentAuthRef.current = true;
    postToCanvas({
      action: 'set',
      data: {
        token,
        userId: me._id,
        expires: new Date(Date.now() + 3600 * 1000).toString(), // Firebase tokens expire in ~1h
        teamId: activeTeamId,
      },
    });
  }, [token, firebaseUser, me?._id, activeTeamId, postToCanvas]);

  // Sync branding — matches production _setBranding format using resolveBranding().
  //
  // Skip when scio3 is loaded inside an iframe (e.g., scio1's engageFrame embeds
  // scio3's /app/engageManagement). Fixes #946.
  //
  // Why: canvas.optisigns.com _setBranding localStorage is shared across all iframes
  // on that origin. When scio1 embeds scio3 as an iframe, BOTH try to sync branding:
  //   - scio1 writes { baseUrl: 'https://app.optisigns.com' }
  //   - scio3 (inside iframe) would write { baseUrl: 'https://app3.optisigns.com' }
  // Last writer wins → polluted baseUrl → clicking AI Designer templates in scio1's
  // Templates page navigates to app3.optisigns.com instead of app.optisigns.com.
  //
  // Only the top-level window owns canvas branding state. Matches scio1's existing
  // guard in canvas-configuration.js:127 (`if (window.parent === window)`).
  const syncBranding = useCallback(() => {
    if (window.parent !== window) return;

    const branding = me?.brandingData ? resolveBranding(me.brandingData) : BRANDING_DEFAULTS;

    postToCanvas({
      action: 'setBranding',
      data: {
        baseUrl: window.location.origin,
        branding: branding.branding,
        colorTheme: branding.colorTheme,
        favicon: branding.favicon,
        isWhiteLabel: branding.isWhiteLabel,
        logo: branding.logo,
        logoCompany: branding.logoCompany,
        removeOptiSignsBranding: branding.removeOptiSignsBranding,
        hideOptiSignsBrandedApps: branding.hideOptiSignsBrandedApps,
        siteTitle: branding.siteTitle,
        templateCategories: branding.templateCategories,
        language: i18n.language,
      },
    });
  }, [me?.brandingData, i18n.language, postToCanvas]);

  // On iframe load, mark ready — effects will handle sync
  const handleLoad = useCallback(() => {
    setIsReady(true);
  }, []);

  // Re-sync auth when token or team changes.
  // syncAuth() internally handles missing auth (sends 'remove' only on actual logout via hasSentAuthRef).
  useEffect(() => {
    if (!isReady) return;
    syncAuth();
  }, [isReady, syncAuth]);

  // Re-sync branding when company or language changes
  useEffect(() => {
    if (!isReady) return;
    syncBranding();
  }, [isReady, syncBranding]);

  // Signal that auth + branding have been synced to the Canvas domain.
  // PersistentCanvasFrame waits for this before loading the Canvas iframe,
  // ensuring Canvas reads valid auth from localStorage on first load.
  // Waits for me._id (not just token) because syncAuth now sends me._id as
  // userId — resolving before me._id is available would let Canvas load
  // before the auth postMessage is sent.
  useEffect(() => {
    if (!isReady || !token || !me?._id) return;
    // Small delay ensures frame.html has written to localStorage
    const timer = setTimeout(() => {
      _authSyncedResolve?.();
      _authSyncedResolve = null;
    }, 500);
    return () => clearTimeout(timer);
  }, [isReady, token, me?._id]);

  return (
    <iframe
      ref={iframeRef}
      src={`${CANVAS_URL}/frame.html`}
      onLoad={handleLoad}
      title="Canvas Auth Bridge"
      className="hidden h-0 w-0 border-0"
    />
  );
}
